import {
  getToken,
  removeToken,
  removeUserCachedDataFromLocalStorage,
} from '@/utils/localStorage';
import type { Method as AxiosMethod } from 'axios';
import axios, { AxiosHeaders } from 'axios';

import { AppConfig } from '@/config';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/constants';
import Path from '@/constants/path';
import _ from 'lodash';

export type ESystemMethod = Extract<
  AxiosMethod,
  | 'get'
  | 'GET'
  | 'delete'
  | 'DELETE'
  | 'post'
  | 'POST'
  | 'put'
  | 'PUT'
  | 'patch'
  | 'PATCH'
  | 'options'
  | 'OPTIONS'
>;

const mainRequestConfig = {
  // Mock baseURL is from a local Postman Mock Server
  baseURL: AppConfig.apiBase,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Authorization: `Api-Key ${AppConfig.apiKey}`,
  },
};

const mainAxiosInstance = axios.create(mainRequestConfig);

mainAxiosInstance.interceptors.request.use(
  config => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const handleTokenInvalid = () => {
  alert('Access token expired. Please log in again.');
  removeToken();
  removeUserCachedDataFromLocalStorage();
  window.location.href = Path.LOGIN;
};

mainAxiosInstance.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const originalRequest = error.config;

    if (error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return mainAxiosInstance(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        handleTokenInvalid();
        return Promise.reject(error);
      }

      try {
        const { data } = await mainAxiosInstance.post('/users/token/refresh/', {
          refresh: refreshToken,
        });
        const newToken = data.token;
        localStorage.setItem(ACCESS_TOKEN_KEY, newToken);
        mainAxiosInstance.defaults.headers.common['Authorization'] =
          'Bearer ' + newToken;
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;

        processQueue(null, newToken);
        return mainAxiosInstance(originalRequest);
      } catch (err) {
        processQueue(err, null);
        handleTokenInvalid();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export const mainRequest = (
  url: string,
  payload: any,
  method: ESystemMethod,
  headers: AxiosHeaders = new AxiosHeaders({
    'X-Requested-With': 'XMLHttpRequest',
  }),
) => {
  const data = payload;
  let params;
  if (method === 'get') {
    params = payload;
  }
  return mainAxiosInstance(url, { data, params, method, headers });
};

const SERVICES = {
  /* <AUTHEN> */
  login: (payload: any) => mainRequest('v1/users/login/', payload, 'post'),
  getProfile: (id: number) => mainRequest(`v1/profile/${id}/`, {}, 'get'),
  updateProfile: (payload: any) =>
    mainRequest(`v1/profile/${payload.id}/`, payload, 'put'),
  changePassword: (payload: any) =>
    mainRequest('v1/users/upd-pwd/', payload, 'put'),

  /* <CUSTOMERS> */
  getCustomers: (payload: any) => mainRequest('v1/customers/', payload, 'get'),
  getAllCustomers: (payload: any) =>
    mainRequest('v1/customer-options/', payload, 'get'),
  getCustomerDetails: (payload: any) =>
    mainRequest(`v1/customers/${payload.id}/`, payload, 'get'),
  createCustomer: (payload: any) =>
    mainRequest(`v1/customers/`, payload, 'post'),
  updateCustomer: (payload: any) =>
    mainRequest(`v1/customers/${payload.id}/`, payload, 'put'),
  deleteCustomer: (payload: any) =>
    mainRequest(`v1/customers/${payload}/`, payload, 'delete'),

  /* <FLOORS> */
  getFloorLevels: (payload: any) => mainRequest('v1/types/', payload, 'get'),
  getFloors: (payload: any) => mainRequest('v1/levels/', payload, 'get'),
  getFloorsOption: (payload: any) =>
    mainRequest('v1/level-options/', payload, 'get'),
  getFloorDetails: (payload: any) =>
    mainRequest(`v1/floors/${payload.id}/`, payload, 'get'),
  createFloor: (payload: any) => mainRequest(`v1/levels/`, payload, 'post'),
  updateFloor: (payload: any) =>
    mainRequest(`v1/levels/${payload.id}/`, payload, 'put'),
  updatePublishStatusFloor: (payload: any) =>
    mainRequest(`v1/upd-level-publish/`, payload, 'post'),
  deleteFloors: (payload: any) =>
    mainRequest(`v1/del-multi-levels/`, payload, 'post'),
  deleteFloor: (payload: any) =>
    mainRequest(`v1/levels/${payload}/`, payload, 'delete'),

  /* <VENUES> */
  getVenueOptions: (payload: any) =>
    mainRequest('v1/venue-options/', payload, 'get'),
  getVenues: (payload: any) => mainRequest('v1/venues/', payload, 'get'),
  getVenuesToCustomer: (payload: any) =>
    mainRequest(`v1/customers/${payload.customerId}/venues/`, payload, 'get'),
  getVenueDetails: (payload: any) =>
    mainRequest(`v1/venues/${payload.id}/`, payload, 'get'),
  createVenue: (payload: any) => mainRequest(`v1/venues/`, payload, 'post'),
  updateVenue: (payload: any) =>
    mainRequest(`v1/venues/${payload.id}/`, _.omit(payload, ['id']), 'put'),
  updatePublishStatusVenue: (payload: any) =>
    mainRequest(`v1/upd-venue-publish/`, payload, 'post'),
  deleteVenues: (payload: any) =>
    mainRequest(`v1/del-multi-venues/`, payload, 'post'),
  deleteVenue: (payload: any) =>
    mainRequest(`v1/venues/${payload}/`, payload, 'delete'),

  /* <CONNECTION> */
  getConnections: (payload: any) =>
    mainRequest(`v1/connections/`, payload, 'get'),

  getConnectionDetails: (payload: any) =>
    mainRequest(`v1/connections/${payload.id}/`, payload, 'get'),
  createConnection: (payload: any) =>
    mainRequest(`v1/connections/`, payload, 'post'),
  updateConnection: (payload: any) =>
    mainRequest(`v1/connections/${payload.id}/`, payload, 'put'),
  deleteConnection: (payload: any) =>
    mainRequest(`v1/connections/${payload}/`, payload, 'delete'),

  /* <LOCATIONS> */
  getLocations: (payload: any) => mainRequest(`v1/locations/`, payload, 'get'),
  getAllLocations: (payload: any) =>
    mainRequest('v1/location-options/', payload, 'get'),

  getLocationDetails: (payload: any) =>
    mainRequest(`v1/locations/${payload.id}/`, payload, 'get'),
  createLocation: (payload: any) =>
    mainRequest(`v1/locations/`, payload, 'post'),
  updateLocation: (payload: any) =>
    mainRequest(`v1/locations/${payload.id}/`, payload, 'put'),
  deleteLocation: (payload: any) =>
    mainRequest(`v1/locations/${payload}/`, payload, 'delete'),

  /* <EVENTS> */
  getEventTags: () => mainRequest(`v1/event-tags/`, null, 'get'),
  getEventTypes: () => mainRequest(`v1/event-types/`, null, 'get'),

  getEvents: (payload: any) => mainRequest(`v1/events/`, payload, 'get'),

  getEventDetails: (payload: any) =>
    mainRequest(`v1/events/${payload.id}/`, payload, 'get'),
  createEvent: (payload: any) => mainRequest(`v1/events/`, payload, 'post'),
  updateEvent: (payload: any) =>
    mainRequest(`v1/events/${payload.id}/`, payload, 'put'),
  deleteEvent: (payload: any) =>
    mainRequest(`v1/events/${payload}/`, payload, 'delete'),

  /* <AMENITY> */
  getAmenities: (payload: any) => mainRequest(`v1/amenities/`, payload, 'get'),
  getLocationAmenities: (payload: any) =>
    mainRequest(`v1/amenities/`, payload, 'get'),
  createAmenity: (payload: any) =>
    mainRequest(`v1/amenities/`, payload, 'post'),
  updateAmenity: (payload: any) =>
    mainRequest(`v1/amenities/${payload.id}/`, payload, 'put'),
  deleteAmenity: (payload: any) =>
    mainRequest(`v1/amenities/${payload}/`, payload, 'delete'),
  setVenueAmenities: (payload: any) => {
    return mainRequest(
      `v1/set-amenities/${payload.venueId}/`,
      _.omit(payload, ['venueId']),
      'put',
    );
  },

  /* <CATEGORY> */
  getCategoriesToVenue: (payload: any) =>
    mainRequest(`v1/categories/`, payload, 'get'),

  createCategory: (payload: any) =>
    mainRequest(`v1/categories/`, payload, 'post'),
  updateCategory: (payload: any) =>
    mainRequest(`v1/categories/${payload.id}/`, payload, 'put'),
  deleteCategory: (payload: any) =>
    mainRequest(`v1/categories/${payload}/`, payload, 'delete'),

  /* <QRCode> */
  getQRCodes: (payload: any) => mainRequest(`v1/qrcodes/`, payload, 'get'),
  getQRCodeDetail: (payload: any) =>
    mainRequest(`v1/qrcodes/${payload.id}/`, payload, 'get'),
  createQRCode: (payload: any) => mainRequest(`v1/qrcodes/`, payload, 'post'),
  updateQRCode: (payload: any) =>
    mainRequest(`v1/qrcodes/${payload.id}/`, payload, 'put'),
  deleteQRCode: (payload: any) =>
    mainRequest(`v1/qrcodes/${payload}/`, payload, 'delete'),

  /* <TAGS> */
  getTags: () => mainRequest(`v1/tags/`, null, 'get'),

  /* <CATEGORIES> */
  getCategories: () => mainRequest(`v1/categories/`, null, 'get'),

  getSnapshots: (payload: any) =>
    mainRequest(`v1/snapshots/${payload.venueId}/`, payload, 'get'),
  revertSnapshotV1: ({ snapshotId }: { snapshotId: string | number }) =>
    mainRequest(`v1/snapshots/revert/${snapshotId}/`, {}, 'delete'),
  revertSnapshotV2: ({ snapshotId }: { snapshotId: string | number }) =>
    mainRequest(`v2/snapshots/${snapshotId}/revert`, {}, 'post'),

  /* <PROVINCE> */
  getProvinceDistrictWard: () =>
    axios({
      url: 'https://raw.githubusercontent.com/kenzouno1/DiaGioiHanhChinhVN/master/data.json',
      method: 'GET',
    }),

  /* <GEO> */
  geoSearch: (payload: any) => mainRequest(`v1/geo-search/`, payload, 'get'),

  /* <CATEGORY> */
  duplicateVenues: (payload: any) =>
    mainRequest(`v1/venues/${payload.id}/clone/`, payload, 'post'),
};

export default SERVICES;
